// Builds a small crossword from a list of words.
// Pure logic, no screen code, so it is easy to test.
//
// Each word is { key, word, translation }, where key is the word in lowercase letters a to z only.
// Returns { width, height, entries } where each entry is
// { key, word, translation, dir: 'across' | 'down', row, col, number, cells: [[row, col], ...] }.

const MAX_SIZE = 11;
const MAX_WORDS = 10;
const ATTEMPTS = 60;

function cellId(r, c) {
    return r + ',' + c;
}

function tryPlace(placed, grid, item, row, col, dir) {
    const dr = dir === 'down' ? 1 : 0;
    const dc = dir === 'across' ? 1 : 0;
    const len = item.key.length;

    // The squares just before and after the word must be empty
    if (grid.has(cellId(row - dr, col - dc))) return null;
    if (grid.has(cellId(row + dr * len, col + dc * len))) return null;

    let crossings = 0;
    for (let i = 0; i < len; i++) {
        const r = row + dr * i;
        const c = col + dc * i;
        const existing = grid.get(cellId(r, c));
        if (existing) {
            if (existing.letter !== item.key[i]) return null;
            // A square may only be shared by one across word and one down word
            if (existing.dirs.includes(dir)) return null;
            crossings += 1;
        } else {
            // A new letter must not touch letters of other words at its sides
            const sideA = grid.has(cellId(r + dc, c + dr));
            const sideB = grid.has(cellId(r - dc, c - dr));
            if (sideA || sideB) return null;
        }
    }
    if (placed.length > 0 && crossings === 0) return null;
    return crossings;
}

function commit(placed, grid, item, row, col, dir) {
    const dr = dir === 'down' ? 1 : 0;
    const dc = dir === 'across' ? 1 : 0;
    const cells = [];
    for (let i = 0; i < item.key.length; i++) {
        const r = row + dr * i;
        const c = col + dc * i;
        const id = cellId(r, c);
        const existing = grid.get(id);
        if (existing) existing.dirs.push(dir);
        else grid.set(id, { letter: item.key[i], dirs: [dir] });
        cells.push([r, c]);
    }
    placed.push({ ...item, dir, row, col, cells });
}

function bounds(placed) {
    let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
    placed.forEach(p => p.cells.forEach(([r, c]) => {
        if (r < minR) minR = r;
        if (r > maxR) maxR = r;
        if (c < minC) minC = c;
        if (c > maxC) maxC = c;
    }));
    return { minR, maxR, minC, maxC };
}

function fits(placed, item, row, col, dir) {
    const dr = dir === 'down' ? 1 : 0;
    const dc = dir === 'across' ? 1 : 0;
    const b = bounds(placed);
    const endR = row + dr * (item.key.length - 1);
    const endC = col + dc * (item.key.length - 1);
    const height = Math.max(b.maxR, endR) - Math.min(b.minR, row) + 1;
    const width = Math.max(b.maxC, endC) - Math.min(b.minC, col) + 1;
    return height <= MAX_SIZE && width <= MAX_SIZE;
}

function shuffled(list) {
    const copy = [...list];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

function attempt(words) {
    // Longer words first make the grid easier to build; a little shuffling keeps puzzles different
    const order = shuffled(words).sort((a, b) => b.key.length - a.key.length + (Math.random() - 0.5) * 3);
    const placed = [];
    const grid = new Map();

    const first = order[0];
    commit(placed, grid, first, 0, 0, 'across');

    for (let w = 1; w < order.length && placed.length < MAX_WORDS; w++) {
        const item = order[w];
        let best = null;
        placed.forEach(other => {
            for (let i = 0; i < item.key.length; i++) {
                for (let j = 0; j < other.key.length; j++) {
                    if (item.key[i] !== other.key[j]) continue;
                    const dir = other.dir === 'across' ? 'down' : 'across';
                    const row = dir === 'down' ? other.row - i : other.row + j;
                    const col = dir === 'down' ? other.col + j : other.col - i;
                    if (!fits(placed, item, row, col, dir)) continue;
                    const crossings = tryPlace(placed, grid, item, row, col, dir);
                    if (crossings === null) continue;
                    const score = crossings + Math.random() * 0.5;
                    if (!best || score > best.score) best = { row, col, dir, score };
                }
            }
        });
        if (best) commit(placed, grid, item, best.row, best.col, best.dir);
    }
    return placed;
}

export function buildCrossword(words) {
    const clean = words.filter(w => w && w.key && w.key.length >= 3);
    if (clean.length === 0) return null;

    let best = null;
    for (let i = 0; i < ATTEMPTS; i++) {
        const placed = attempt(clean);
        if (!best || placed.length > best.length) best = placed;
        if (best.length >= MAX_WORDS) break;
    }

    const b = bounds(best);
    const entries = best.map(p => {
        const cells = p.cells.map(([r, c]) => [r - b.minR, c - b.minC]);
        return { key: p.key, word: p.word, translation: p.translation, dir: p.dir, row: p.row - b.minR, col: p.col - b.minC, cells };
    });

    // Numbers go to starting squares, left to right and top to bottom; an across and a down word can share one
    const starts = {};
    entries.forEach(e => { starts[cellId(e.row, e.col)] = true; });
    const startList = Object.keys(starts)
        .map(id => id.split(',').map(Number))
        .sort((a, b2) => a[0] - b2[0] || a[1] - b2[1]);
    const numberAt = {};
    startList.forEach(([r, c], i) => { numberAt[cellId(r, c)] = i + 1; });
    entries.forEach(e => { e.number = numberAt[cellId(e.row, e.col)]; });
    entries.sort((a, b2) => a.number - b2.number);

    return { width: b.maxC - b.minC + 1, height: b.maxR - b.minR + 1, entries };
}